/**
 * PLAT-5624 — appointment-type GROUPS for the appointments embed.
 *
 * QA: "Appointment embed is not pulling in the group details." Step 1 rendered
 * a flat list in which "60-Minute Massage" appeared once per provider, because
 * appointment types live one row per (org, provider) — each provider owns their
 * own copy with its own uuid, price, duration and `stripeProductId` — and the
 * embed threw the types' group tags away.
 *
 * This module is the pure merge: it folds those per-provider copies into one
 * card per group ("Wellness"), so the flow becomes
 *   group → providers in that group → that provider's type(s) → times.
 *
 * Ported from the shipping reference implementation,
 * `libs/shared-ux/src/site-builder/blocks/appointments/ZipperSchedulingBlockRevamp.tsx`
 * (:50-97), with three deliberate corrections:
 *
 *  1. **Buckets are derived from the SERVICES, not seeded from the org
 *     roster.** The reference seeds `_groups` from
 *     `org.attributes.appointmentGroups` and then silently drops any tag whose
 *     title is not in that roster. The roster is a best-effort mirror written
 *     on save, and `session-org-anon` returns nothing at all for an org with no
 *     published site — so seeding from it makes an embed-only org's groups
 *     vanish entirely. Here the roster is used for ORDERING and nothing else.
 *  2. **A group that resolves to a single provider is collapsed** back into the
 *     ungrouped list. Drilling into a group to pick the only provider in it is
 *     a wasted click.
 *  3. **Ungrouped types are returned, not discarded**, so they keep rendering
 *     as normal directly-bookable rows alongside the group cards.
 *
 * Matching is by **`title`, never `id`** — see `AppointmentGroupTag`.
 */
import type {
  AppointmentGroupTag,
  AppointmentProvider,
  AppointmentService,
} from './data/mock-appointments';
import { formatShortAmount } from './format-money';

/** One provider inside a group, with the type(s) they offer under it. */
export interface AppointmentGroupProvider {
  provider: AppointmentProvider;
  /** This provider's own copies of the types tagged into the group. */
  services: AppointmentService[];
}

export interface AppointmentGroup {
  /** Stable shared-parent identity, or normalized legacy-title fallback. */
  id: string;
  title: string;
  /** Providers offering something in this group, sorted by display name. */
  providers: AppointmentGroupProvider[];
  /** Every member type across every provider (merged price/duration/image). */
  services: AppointmentService[];
}

export interface AppointmentGroupsResult {
  groups: AppointmentGroup[];
  /**
   * Types that are not represented by any surviving group card — either
   * untagged, or tagged only into groups that collapsed to a single provider.
   * These must keep rendering as ordinary service rows: an org with zero
   * grouped types produces `groups: []` and `ungrouped === every service`.
   */
  ungrouped: AppointmentService[];
}

/**
 * The provider user ids that can deliver `s`.
 *
 * `providerUserId` — the config row this concrete type/price/product came from
 * — is authoritative. `providersByService` is the fallback for fixtures or
 * legacy data without row ownership. This order matters when two providers
 * reuse one appointment-type id: applying the shared mapping to each preserved
 * row would put both providers' prices under both providers.
 */
function providerKeysFor(
  s: AppointmentService,
  providersByService?: Record<string, number[]>,
  allowedProviderIds?: ReadonlySet<string>,
): string[] {
  const mapped = providersByService?.[s.id];
  const keys =
    s.providerUserId != null
      ? [String(s.providerUserId)]
      : mapped?.length
        ? mapped.map((n) => String(n))
        : [];
  if (allowedProviderIds) {
    return keys.filter((key) => allowedProviderIds.has(key));
  }
  return keys;
}

/** Distinct, trimmed group titles carried by a service (order preserved). */
function titlesOf(s: AppointmentService): string[] {
  const titles: string[] = [];
  for (const tag of s.groups ?? []) {
    const title = (tag?.title ?? '').trim();
    if (!title || titles.includes(title)) continue;
    titles.push(title);
  }
  return titles;
}

type GroupIdentity = { id: string; title: string };

/** Shared parents supersede tags; unresolved legacy rows still fall back. */
function groupIdentitiesOf(s: AppointmentService): GroupIdentity[] {
  if (s.sharedAppointmentTypeId) {
    return [
      {
        id: `shared-appointment-group:${s.sharedAppointmentTypeId}`,
        title: s.name,
      },
    ];
  }
  return titlesOf(s).map((title) => ({
    id: `legacy-appointment-group:${title
      .replace(/\s+/g, ' ')
      .toLocaleLowerCase()}`,
    title: title.replace(/\s+/g, ' '),
  }));
}

/**
 * Merge per-provider appointment types into group cards.
 *
 * @param services  The services ALREADY narrowed by the embed's builder
 *                  pre-filters (`filterServiceIds` / `filterInstructors`) —
 *                  grouping must never resurrect a filtered-out type.
 * @param providers The provider list (the generic "First Available" entry is
 *                  ignored: it cannot resolve to a concrete appointment type,
 *                  price or Stripe product across providers).
 * @param orgGroups The org's mirrored roster — ORDERING ONLY, never an
 *                  allow-list. A title absent from it still produces a group;
 *                  a title present in it but unused by any service does not.
 * @param providersByService appointmentTypeId → provider user ids.
 * @param allowedProviderIds provider pre-filter, when configured. This must
 *                  narrow the provider mapping itself: filtering only the
 *                  service row is insufficient when one appointment-type id
 *                  maps to several providers, because the unfiltered mapping
 *                  would re-add an excluded provider to the group bucket.
 */
export function buildAppointmentGroups(
  services: AppointmentService[],
  providers: AppointmentProvider[],
  orgGroups?: AppointmentGroupTag[],
  providersByService?: Record<string, number[]>,
  allowedProviderIds?: ReadonlySet<string>,
): AppointmentGroupsResult {
  const providerById = new Map<string, AppointmentProvider>();
  for (const p of providers) {
    if (!p.generic) providerById.set(p.id, p);
  }

  /** stable group id → display title and provider members. */
  const buckets = new Map<
    string,
    { title: string; providers: Map<string, AppointmentGroupProvider> }
  >();
  /**
   * Concrete service row → the group titles it was tagged with. This must be
   * keyed by row, not bare appointment-type id: different providers can own
   * distinct price/product rows that reuse the same id.
   */
  const tagged = new Map<AppointmentService, string[]>();

  for (const s of services) {
    const identities = groupIdentitiesOf(s);
    if (!identities.length) continue;
    tagged.set(
      s,
      identities.map(({ id }) => id),
    );

    const keys = providerKeysFor(
      s,
      providersByService,
      allowedProviderIds,
    );
    for (const identity of identities) {
      const bucket = buckets.get(identity.id) ?? {
        title: identity.title,
        providers: new Map<string, AppointmentGroupProvider>(),
      };
      for (const key of keys) {
        const entry = bucket.providers.get(key) ?? {
          // A provider missing from the filter-data payload still has to be
          // selectable — fall back to a minimal stub rather than losing the
          // whole branch of the tree.
          provider: providerById.get(key) ?? { id: key, name: 'Provider' },
          services: [] as AppointmentService[],
        };
        if (!entry.services.some((x) => x.id === s.id)) entry.services.push(s);
        bucket.providers.set(key, entry);
      }
      buckets.set(identity.id, bucket);
    }
  }

  /** Roster position per title — ordering metadata only. */
  const rosterOrder = new Map<string, number>();
  (orgGroups ?? []).forEach((g, i) => {
    const title = (g?.title ?? '').trim();
    const id = `legacy-appointment-group:${title
      .replace(/\s+/g, ' ')
      .toLocaleLowerCase()}`;
    if (title && !rosterOrder.has(id)) rosterOrder.set(id, i);
  });

  const groups: AppointmentGroup[] = [];
  for (const [id, bucket] of buckets) {
    // Correction 2: one provider (or none resolvable) is not worth a screen.
    if (bucket.providers.size < 2) continue;
    const providerEntries = [...bucket.providers.values()].sort((a, b) =>
      (a.provider.name || '').localeCompare(b.provider.name || ''),
    );
    for (const entry of providerEntries) {
      entry.services.sort((a, b) => a.name.localeCompare(b.name));
    }
    groups.push({
      id,
      title: bucket.title,
      providers: providerEntries,
      services: providerEntries.flatMap((e) => e.services),
    });
  }

  const UNRANKED = Number.MAX_SAFE_INTEGER;
  groups.sort((a, b) => {
    const ai = rosterOrder.get(a.id) ?? UNRANKED;
    const bi = rosterOrder.get(b.id) ?? UNRANKED;
    return ai !== bi ? ai - bi : a.title.localeCompare(b.title);
  });

  const survived = new Set(groups.map((g) => g.id));
  const ungrouped = services.filter((s) => {
    const titles = tagged.get(s);
    if (!titles?.length) return true; // never grouped
    return !titles.some((id) => survived.has(id)); // every bucket collapsed
  });

  return { groups, ungrouped };
}

/**
 * The price to show on a MERGED row (a group card, or a provider row inside a
 * group), where the members can disagree.
 *
 *  - every member the same paid price → that price (`$110`, `£110`)
 *  - members disagree                 → `From $X` over the PAID members
 *  - a paid member plus a credit/free one → `From $X` (never "$X flat", which
 *    would misprice, and never "Free", which would imply no cost)
 *  - nothing paid, all explicitly free → no label at all
 *  - nothing paid, otherwise           → `Use credits`
 *
 * The free/credit split mirrors `formatServicePrice`: `price: 0` alone cannot
 * distinguish a genuinely free service from a credit-priced one, only the
 * org's own `isFree` switch can — and even that flag tracks "has no drop-in
 * fee" closely enough that a merged row must not announce "Free". An empty
 * label is the honest answer; the caller drops the price slot entirely.
 */
export function mergedPriceLabel(
  services: AppointmentService[],
  currency?: string | null,
): string {
  const paid = services.map((s) => s.price ?? 0).filter((p) => p > 0);
  if (!paid.length) {
    return services.length > 0 && services.every((s) => s.isFree === true)
      ? ''
      : 'Use credits';
  }
  const min = Math.min(...paid);
  const uniform = paid.length === services.length && paid.every((p) => p === min);
  const label = formatShortAmount(min, currency);
  return uniform ? label : `From ${label}`;
}

/** `60 min` when the members agree, `45–60 min` when they don't. */
export function mergedDurationLabel(services: AppointmentService[]): string {
  const mins = services
    .map((s) => s.duration)
    .filter((d): d is number => typeof d === 'number' && Number.isFinite(d));
  if (!mins.length) return '';
  const lo = Math.min(...mins);
  const hi = Math.max(...mins);
  return lo === hi ? `${lo} min` : `${lo}–${hi} min`;
}

/**
 * A merged row has no image of its own — borrow the first member's. The caller
 * falls back to the shared photo GLYPH (never a letter from the title) when
 * this returns undefined.
 */
export function mergedImage(
  services: AppointmentService[],
): string | undefined {
  return services.find((s) => !!s.thumbnail)?.thumbnail;
}
